/* eslint no-underscore-dangle: 0 */
/*eslint no-multi-assign: 0 */

import qs from 'qs';
import { paramsToQueryString } from 'utils/url';

export const createQuery = query =>
  qs.stringify(query, { addQueryPrefix: true, skipNulls: true });
/**
 * Routes contains all our Rails endpoints to be utilized globally
 * throughout our React Components.
 *
 * @example Usage of a route to make a request to the endpoint
 * request.get(routes.FEEDBACK_CRITERIA.CRITERIA)
 *   .then(payload => {
 *     // success action here
 *   })
 *   .fail(() => {
 *     // failure action here
 *   });
 */
export const routes = {
  NOT_FOUND: '/404',
  ARCHIVES: (params = {}) =>
    `/archives${paramsToQueryString({
      page: params.page,
      type: params.type,
      query: params.query,
    })}`,
  BETA_FEEDBACK: '/beta_feedback',
  BULK_ARCHIVE: '/archives/bulk_archive',
  BULK_RESTORE: '/archives/bulk_restore',
  CONTENT: {
    INDEX: '/content',
    LIBRARY: '/content/library',
    LIBRARY_ELEMENTS: '/content/library/elements',
    SEARCH: '/content/search',
    LIBRARY_TEMPLATES: '/content/library/templates',
    SKILL_TAGS: '/content/skill_tags',
  },
  NEW_LESSON: '/lessons/new',
  CREATE_LESSON: '/lessons',
  LESSON: id => `/lessons/${id}`,
  LESSON_ASSIGNMENT: id => `/lesson/${id}`,
  LESSON_ADD_OWNER: id => `/lessons/${id}/owners`,
  LESSON_REMOVE_OWNER: (id, ownerId) => `/lessons/${id}/owners/${ownerId}`,
  LESSON_ANSWER: id => `/lesson/${id}/answer`,
  LESSON_ATTEMPTS: id => `/lesson/${id}/attempts`,
  LESSON_MISSED: (id, query) => `/lesson/${id}/missed${createQuery(query)}`,
  LESSON_RETAKE: id => `/lesson/${id}/retake`,
  LESSON_REMOVE_ATTEMPT: (lessonId, attemptId, questionId) =>
    `/lesson/${lessonId}/attempts/${attemptId}${createQuery({
      question_id: questionId,
    })}`,
  LIBRARY_PATH_LESSON_MISSED: (pathId, lessonId, query) =>
    `/library/path/${pathId}/lesson/${lessonId}/missed${createQuery(query)}`,
  LIBRARY_PATH_LESSON_RETAKE: (pathId, lessonId) =>
    `/library/path/${pathId}/lesson/${lessonId}/retake`,
  LIBRARY_PATH: (pathId, query) =>
    `/library/path/${pathId}${createQuery(query)}`,
  LIBRARY_LESSON_MISSED: (lessonId, query) =>
    `/library/lesson/${lessonId}/missed${createQuery(query)}`,
  LIBRARY_LESSON_RETAKE: lessonId => `/library/lesson/${lessonId}/retake`,
  PROMOTE_TO_ANSWER: (lessonId, attemptId) =>
    `/lesson/${lessonId}/attempts/${attemptId}/promote_to_answer`,
  CREATE_TEMPLATE: '/templates',
  CREATE_TEMPLATE_FROM_LESSON: '/templates/create_from_lesson',
  EDIT_TEMPLATE: templateId => `/templates/${templateId}/edit`,
  ARCHIVE_TEMPLATE: templateId => `/templates/${templateId}/archive`,
  PREVIEW_TEMPLATE: templateId => `/preview/template/${templateId}`,
  USE_TEMPLATE: templateId => `/templates/${templateId}/use`,
  TEMPLATE_GALLERY: '/templates/gallery',
  LESSON_OVERVIEW: {
    ASSIGNMENT_COMPLETION: (lessonId, groupId) =>
      `/lessons/${lessonId}/assignment_completion${paramsToQueryString({
        group_id: groupId,
      })}`,
    ALL_QUESTION_METRICS: (lessonId, filter, groupId) =>
      `/lessons/${lessonId}/question_metrics${paramsToQueryString({
        filter_by: filter,
        group_id: groupId,
      })}`,
    OPEN_ENDED_ELEMENTS: (lessonId, groupId, page, perPage) =>
      `/lessons/${lessonId}/overview/elements/open_ended${paramsToQueryString({
        group_id: groupId,
        page,
        per_page: perPage,
      })}`,
    DISTRIBUTORS_COMPANIES: lessonId =>
      `/lessons/${lessonId}/distributors/companies`,
    DISTRIBUTE_TO_COMPANY: lessonId =>
      `/lessons/${lessonId}/distributors/company`,
    ENGAGEMENT: (lessonId, groupId) =>
      `/lessons/${lessonId}/engagement${paramsToQueryString({
        group_id: groupId,
      })}`,
    REVOKE_FROM_COMPANY: lessonId => `/lessons/${lessonId}/revoke`,
    DISTRIBUTORS: lessonId => `/lessons/${lessonId}/distributors`,
    POTENTIAL_DISTRIBUTORS: lessonId =>
      `/lessons/${lessonId}/distributors/people`,
    DELETE_DISTRIBUTOR: (lessonId, distributorType, distributorId) =>
      `/lessons/${lessonId}/distributors/${distributorType}/${distributorId}`,
    EXPERT: lessonId => `/lessons/${lessonId}/expert`,
    EXPERT_SUGGESTIONS: lessonId =>
      `/ownerships/experts?ownable_id=${lessonId}&ownable_type=Lesson`,
    PRACTICE_INSIGHTS: (lessonId, groupId, page, perPage) =>
      `/lessons/${lessonId}/practice_insights${paramsToQueryString({
        group_id: groupId,
        page,
        per_page: perPage,
      })}`,
    QUESTION_METRICS: (lessonId, questionId, element, groupId) =>
      `/lessons/${lessonId}/question_metrics/${questionId}${paramsToQueryString(
        {
          element,
          group_id: groupId,
        },
      )}`,
    CONVERSATION_COMPLETION_METRICS: (lessonId, groupId) =>
      `/lessons/${lessonId}/conversation_completion${paramsToQueryString({
        group_id: groupId,
      })}`,
    CONVERSATION_COMPLETIONS_BY_USER: (lessonId, questionId, groupId) =>
      `/lessons/${lessonId}/questions/${questionId}/completions_by_user${paramsToQueryString(
        {
          group_id: groupId,
        },
      )}`,
    RATINGS_LIKED: (lessonId, groupId) =>
      `/lessons/${lessonId}/ratings/liked${paramsToQueryString({
        group_id: groupId,
      })}`,
    RATINGS_DISLIKED: (lessonId, groupId) =>
      `/lessons/${lessonId}/ratings/disliked${paramsToQueryString({
        group_id: groupId,
      })}`,
    RATINGS: (lessonId, groupId) =>
      `/lessons/${lessonId}/lesson_ratings${paramsToQueryString({
        group_id: groupId,
      })}`,
    ROLE_PLAY_FEEDBACKS: (lessonId, rolePlayId, groupId) =>
      `/lessons/${lessonId}/role_plays/${rolePlayId}${paramsToQueryString({
        group_id: groupId,
      })}`,
    ROLE_PLAY_FEEDBACKS_DATA: (lessonId, rolePlayId, groupId, page, perPage) =>
      `/lessons/${lessonId}/role_plays/${rolePlayId}/feedbacks${paramsToQueryString(
        {
          group_id: groupId,
          page,
          per_page: perPage,
        },
      )}`,
    QUESTION: (lessonId, questionId, groupId) =>
      `/lessons/${lessonId}/questions/${questionId}${paramsToQueryString({
        group_id: groupId,
      })}`,
    ASSIGNMENT_SUMMARY: (lessonId, groupId) =>
      `/lessons/${lessonId}/assignment_summary${paramsToQueryString({
        group_id: groupId,
      })}`,
    ASSIGNMENT_STATUS: (lessonId, status, groupId) =>
      `/lessons/${lessonId}/assignments/${status}${paramsToQueryString({
        group_id: groupId,
      })}`,
    GRADER_OPTIONS: lessonId => `/lessons/${lessonId}/grader_options`,
    LESSON_GRADERS: lessonId => `/lessons/${lessonId}/graders`,
    LESSON_GRADER: (lessonId, graderId) =>
      `/lessons/${lessonId}/graders/${graderId}`,
    SCORM_ANALYTICS: (lessonId, groupId) =>
      `/lessons/${lessonId}/scorm_analytics${paramsToQueryString({
        group_id: groupId,
      })}`,
    CLONE_LESSON: lessonId => `/lessons/${lessonId}/clone`,
    ARCHIVE_LESSON: lessonId => `/lessons/${lessonId}/archive`,
    RESTORE_LESSON: lessonId => `/lessons/${lessonId}/restore`,
    EDIT_LESSON: lessonId => `/lessons/${lessonId}/edit`,
    EDIT_LESSON_SECTION: (lessonId, sectionId) =>
      `/lessons/${lessonId}/sections/${sectionId}/edit`,
    PREVIEW_LESSON: lessonId => `/preview/lesson/${lessonId}`,
    PREVIEW_LESSON_DRAFT: lessonId =>
      `/preview/lesson/${lessonId}?version=draft`,
    PREVIEW_LESSON_SECTION: (lessonId, sectionId) =>
      `/preview/lesson/${lessonId}?section_id=${sectionId}`,
    PREVIEW_LESSON_SECTION_ELEMENT: (lessonId, sectionId, elementId) =>
      `/preview/lesson/${lessonId}?section_id=${sectionId}#${elementId}`,
    PREVIEW_LESSON_SECTION_DRAFT: (lessonId, draftSectionId) =>
      `/preview/lesson/${lessonId}?section_id=${draftSectionId}&version=draft`,
    PREVIEW_LESSON_SECTION_ELEMENT_DRAFT: (
      lessonId,
      draftSectionId,
      elementId,
    ) =>
      `/preview/lesson/${lessonId}?version=draft&section_id=${draftSectionId}#${elementId}`,
    INDEX: lessonId => `/lessons/${lessonId}`,
  },
  LEARNING: {
    REPORT_CARD: {
      LESSON_ROLE_PLAY_FEEDBACKS: lessonId =>
        `/lesson/${lessonId}/report/role_play_feedbacks`,
      LESSON_RECAP: lessonId => `/lesson/${lessonId}/report/recap`,
      PATH_LESSON_ROLE_PLAY_FEEDBACKS: (pathId, lessonId) =>
        `/path/${pathId}/lesson/${lessonId}/report/role_play_feedbacks`,
      LIBRARY_LESSON_ROLE_PLAY_FEEDBACKS: lessonId =>
        `/library/lesson/${lessonId}/report/role_play_feedbacks`,
      LIBRARY_PATH_LESSON_ROLE_PLAY_FEEDBACKS: (pathId, lessonId) =>
        `/library/path/${pathId}/lesson/${lessonId}/report/role_play_feedbacks`,
    },
    LESSON_EVENT: lessonEventId =>
      `/lesson_event/${lessonEventId}${window.location.search}`,
  },
  PEOPLE: '/people',
  USER: id => `/users/${id}`,
  USERS_INTEGRATIONS: id => `/users/${id}/integrations`,
  USER_INTEGRATION: (id, integrationId) =>
    `/users/${id}/integrations/${integrationId}`,
  NEW_BULK_IMPORT: '/users/imports/new',
  SIGNOUT: '/signout',
  SETTINGS: {
    INDEX: '/settings',
    UPDATE_USER_SETTINGS: '/settings/update_user_settings',
    UPDATE_USER: '/settings/update_user',
    UPDATE_COMPANY: '/settings/update_company',
    INTEGRATIONS: '/settings/integrations',
    GENERATE_ZENDESK_SECURITY_KEY: '/settings/generate_zendesk_security_key',
    GENERATE_API_KEY: '/settings/generate_api_key',
    GENERATE_SALESFORCE_CANVAS_KEY: '/settings/generate_salesforce_canvas_key',
    MY_ACCOUNT: '/settings/my_account',
    CHANGE_PASSWORD: 'settings/change_password',
  },
  SMART_GROUPS: '/smart_groups',
  SKILLS: {
    INDEX: '/skills',
    GRAPHQL: '/skills/graphql',
    COACHING_INDEX: '/coaching/skills',
    LIBRARY: '/coaching/skills/library',
    PROFILES: '/coaching/skills/profiles',
    PROFILE_ADD: '/coaching/skills/profile/add',
  },
  GRADES: {
    UPDATE: progressId => `/grades/${progressId}`,
    INDEX: (gradableId, gradableType) =>
      `/grades${paramsToQueryString({
        gradable_id: gradableId,
        gradable_type: gradableType,
      })}`,
    INDEX_V2: (gradableId, gradableType) =>
      `/grades/${gradableType}/${gradableId}`,
    DISPLAY_GRADES_PENDING: '/grades/display_grades_pending',
    QUESTION_FEEDBACK_COMMENTS: questionId =>
      `/questions/${questionId}/comments`,
    LESSONS_LIST: (page, perPage, query) =>
      `/grades/lessons/search${paramsToQueryString({
        page,
        per_page: perPage,
        query,
      })}`,
    LONG_WAITING_LIST: (page, perPage, query) =>
      `/grades/long_waiting/search${paramsToQueryString({
        page,
        per_page: perPage,
        query,
      })}`,
    USERS_LIST: (page, perPage, query) =>
      `/grades/users/search${paramsToQueryString({
        page,
        per_page: perPage,
        query,
      })}`,
    GROUPS_LIST: (page, perPage, query) =>
      `/groups${paramsToQueryString({
        page,
        per_page: perPage,
        q: query,
      })}`,
    LESSON_PROGRESSES: (id, page) =>
      `/grades/lessons/${id}${paramsToQueryString({
        page,
      })}`,
    USER_PROGRESSES: (id, page) =>
      `/grades/users/${id}${paramsToQueryString({
        page,
      })}`,
    GROUP_PROGRESSES: (id, page) =>
      `/grades/groups/${id}${paramsToQueryString({
        page,
      })}`,
    LONG_WAITING_PROGRESS: id => `/grades/long_waiting/${id}`,
  },
  LESSON_GRADEBOOK: id => `/gradebook?lesson_ids[]=${id}`,
  GRADEBOOK: '/gradebook',
  GROUPS: '/groups',
  GROUP_SHOW: id => `/groups/${id}`,
  NEW_GROUP: '/groups/new',
  GROUP_EVALUATORS: id => `/groups/${id}/evaluators`,
  DELETE_GROUP_EVALUATOR: (groupId, evaluatorId) =>
    `/groups/${groupId}/evaluators/${evaluatorId}`,
  NEW_USER: '/users/new',
  ASSETS_BLANK_SLATES: '/assets/blankslates',
  EVENTS: '/events',
  ASYNC_REPORTS_REQUEST: '/async_reports',
  OWNERSHIPS: '/ownerships',
  OWNERSHIPS_PEOPLE: '/ownerships/people',
  OWNERSHIPS_EXPERTS: '/ownerships/experts',
  OWNERSHIP: id => `/ownerships/${id}`,
  TAGGINGS: '/taggings',
  ALL_TAGGINGS: (type, id) => `/taggings?taggableType=${type}&taggableId=${id}`,
  TAGGING: id => `/taggings/${id}`,
  TAGS: '/tags',
  TAGS_DELETE: id => `/tags/${id}`,
  TAGS_UPDATE: id => `/tags/${id}`,
  TAGS_EDIT: id => `/tags/${id}/edit`,
  TAGS_NEW: '/tags/new',
  TAG_ALL_CONTENT: '/taggings/tag_all_contents',
  VERSIONS: {
    INDEX: lessonId => `/lessons/${lessonId}/versions`,
  },
  ASSIGNMENT_ASSIGN: '/assignments/assign',
  ASSIGNMENT_ADDED_CONTENTS_PREVIEW: id =>
    `/assignments/added_contents_preview/${id}`,
  ASSIGNMENT_BULK_REMIND: '/assignments/bulk_remind',
  SCHEDULED_ASSIGNMENTS: '/scheduled_assignments',
  EMAIL_PREVIEW: '/assignments/email_preview',
  NOTIFICATIONS_LATEST: '/notifications/latest',
  PATHS: '/paths',
  NEW_PATH: '/paths/new',
  PATH: id => `/paths/${id}`,
  PREVIEW_LEARNER_PATH: (id, query = {}) =>
    `/preview/path/${id}${createQuery(query)}`,
  LEARNER_PATH: (id, query = {}) => `/path/${id}${createQuery(query)}`,
  PATH_COPY: id => `/paths/${id}/copy`,
  PATH_DISTRIBUTORS: id => `/paths/${id}/distributors`,
  PATH_POTENTIAL_DISTRIBUTORS: id => `/paths/${id}/distributors/people`,
  PATH_REMOVE_DISTRIBUTORS: (pathId, type, id) =>
    `/paths/${pathId}/distributors/${type}/${id}`,
  PATH_ARCHIVE: id => `/paths/${id}/archive`,
  PATH_RESTORE: id => `/paths/${id}/restore`,
  PATH_EDIT: id => `/paths/${id}/edit`,
  PATH_PREVIEW: id => `/preview/path/${id}`,
  PATH_VIEW: id => `/paths/${id}/view`,
  PUBLISH_PATH: id => `/paths/${id}/publish`,
  PATH_REPORTING: {
    INDEX: id => `/paths/${id}/reporting`,
    ASSIGNMENTS: id => `/paths/${id}/reporting/assignment_stats`,
    LIBRARY: id => `/paths/${id}/reporting/library_stats`,
    PERFORMANCE: id => `/paths/${id}/reporting/performance_stats`,
    ANCESTORS: id => `/paths/${id}/reporting/ancestors`,
  },
  PATH_ADD_CONTENT: '/paths/add_content',
  PATH_ADD_CONTENT_CERTIFICATION: '/paths/add_content?certification=true',
  PATH_ADD_OWN_CONTENT: id => `/paths/${id}/add_content`,
  PATH_COMPLETION_TIME: id => `/paths/${id}/estimated_seconds_to_complete`,
  PATH_LESSON_MISSED: (pathId, lessonId, query) =>
    `/path/${pathId}/lesson/${lessonId}/missed${createQuery(query)}`,
  PATH_LESSON_RETAKE: (pathId, lessonId) =>
    `/path/${pathId}/lesson/${lessonId}/retake`,
  PERMISSIONS: ({ debug = false }) =>
    `/permissions${debug ? createQuery({ debug_permissions: true }) : ''}`,
  ASSIGNABLES: {
    USER_COUNT: '/assignments/assign/users_count',
    CONTENT: '/assignments/contents',
    ASSIGNEES: '/assignments/people',
    GROUP_ASSIGNEES: '/assignments/people?filter[type][]=Group',
  },
  ROLES: '/roles',
  ROLE: roleId => `/roles/${roleId}`,
  ROLE_PERMISSIONS: roleId => `/roles/${roleId}/permissions`,
  ROLE_OWNERSHIPS: roleId => `/roles/${roleId}/ownerships`,
  MANAGED_ROLES: {
    INDEX: roleId => `roles/${roleId}/managed_roles`,
    CREATE: roleId => `roles/${roleId}/managed_roles`,
    DELETE: roleId => `roles/${roleId}/managed_roles`,
  },
  PERMISSION: (roleId, permissionName) =>
    `/roles/${roleId}/permission/${permissionName}`,
  SCORM_DATA: id => `/scorms/${id}`,
  SCORM_UPLOADED: id => `/scorms/${id}/record_upload`,
  SCORM_UPLOAD_OPTIONS: '/scorms/upload_options',
  SCORM_START: id => `/scorms/${id}/start_upload`,
  SCORM_STATUS: id => `/scorms/${id}/upload_status`,
  SCORM_CANCEL: id => `/scorms/${id}/cancel_upload`,
  SCORM_FILES: id => `/lessons/${id}/scorm_files`,
  WEB_WIDGETS: '/web_widgets',
  WEB_WIDGETS_FILTER: '/web_widgets/filter',
  RELATED_URLS: '/related_urls',
  RELATED_URLS_FILTER: '/related_urls/filter',
  RELATED_URL_MAPPING: id => `/related_url_mappings/${id}`,
  RELATED_URL_MAPPINGS: '/related_url_mappings',
  LIBRARY_CONTENTS: '/library_contents',
  LIBRARY_CONTENT: id => `/library_contents/${id}`,
  SAVED_ELEMENT_CONTENTS: '/saved_element_contents',
  COPY_SAVED_ELEMENT_CONTENT: (lessonId, sectionId, id) =>
    `/lessons/${lessonId}/sections/${sectionId}/saved_element_contents/${id}`,
  TEMPLATE_COPY_SAVED_ELEMENT_CONTENT: (templateId, sectionId, id) =>
    `/templates/${templateId}/sections/${sectionId}/saved_element_contents/${id}`,
  SAVED_ELEMENT_CONTENT: id => `/saved_element_contents/${id}`,
  FEEDBACK_CRITERIA: {
    CRITERIA: '/criteria',
    CUSTOM_CRITERION: '/custom_criterion',
  },
  ASSOCIATED_IMAGES: '/associated_images',
  IMAGES_UPLOAD_OPTIONS: '/images/upload_options',
  ANSWER_UPLOAD_DOWNLOAD_URL_FOR_QUESTION_ID: questionId =>
    `/answer_upload/${questionId}/question_download`,
  ANSWER_UPLOAD_DOWNLOAD_URL_FOR_ANSWER_ID: answerId =>
    `/answer_upload/${answerId}/answer_download`,
  ANSWER_UPLOAD_OPTIONS: '/answer_upload/upload_options',
  ANSWER_UPLOAD: questionId => `/answer_upload/${questionId}`,
  ANSWER_FROM_LEARNER_SEEKING_FEEDBACK: answerId =>
    `/answers/${answerId}/learner_seeking_feedback`,
  DOCUMENTS_UPLOAD_OPTIONS: id => `/documents/${id}/upload_options`,
  DOCUMENTS_RECORD_UPLOAD: id => `/documents/${id}/record_upload`,
  IMAGES: '/images',
  IMAGES_NEW_IMAGE: '/images/new_image',
  IMAGE: id => `/images/${id}`,
  IMAGE_LIBRARY_VISIBILITY_ADD: imageId =>
    `/images/${imageId}/library_visibility/add`,
  IMAGE_LIBRARY_VISIBILITY_REMOVE: imageId =>
    `/images/${imageId}/library_visibility/remove`,
  IMAGES_DESTROY_ALL: '/images/destroy_image',
  LEARN: '/learn',
  LEARN_ASSIGNMENTS: '/learn/assignments',
  LEARN_RESOURCES: '/learn/resources',
  LEARN_SEARCH: '/learn/search',
  LEARN_SEARCH_TRACK: '/learn/search_events/',
  LEARN_SPOTLIGHTS: '/learn/spotlights',
  LEARN_TOPICS: '/learn/topics',
  LEARN_TOPIC: '/learn/topics/:id(\\d+)(.*)',
  LEARN_ASK_THE_EXPERT: '/learn/ask_the_expert',
  LEARN_HISTORY: '/learn/history',
  LEARN_COMPLETED_CERTIFICATIONS: query =>
    `/learn/completed_certifications${createQuery(query)}`,
  FEATURED_CONTENTS: '/featured_contents',
  FEATURED_CONTENT: id => `/featured_contents/${id}`,
  VIDEO_RESPONSE_TECHNICAL_ISSUES: questionId =>
    `/video_response/questions/${questionId}/technical_issues`,
  CREATE_QUESTION_FIELD: questionId =>
    `/questions/${questionId}/question_fields`,
  QUESTIONS: {
    ADD_RESPONSE: (questionId, interactionId) =>
      `/questions/${questionId}/interactions/${interactionId}/responses`,
    FEEDBACK: questionId => `/questions/${questionId}/comments`,
    GOOD_EXAMPLE: questionId => `/questions/${questionId}/example_recordings`,
    GOOD_EXAMPLE_RECORDING: (questionId, recordingId) =>
      `/questions/${questionId}/example_recordings/${recordingId}`,
    INTERACTIONS: (questionId, interactionId) =>
      `/questions/${questionId}/interactions/${interactionId}`,
    INTERACTIONS_CREATE: questionId => `/questions/${questionId}/interactions`,
    RESPONSES: (questionId, interactionId, responseId) =>
      `/questions/${questionId}/interactions/${interactionId}/responses/${responseId}`,
    METADATUM: (questionId, metadatumId) =>
      `/questions/${questionId}/metadata/${metadatumId}`,
    NEW_CHOICE: questionId => `/questions/${questionId}/choices`,
  },
  INSIGHTS: {
    INDEX: '/insights',
    IN_PERSON_TRAINING: {
      REPORT: '/insights/in_person_training_report.csv',
    },
    RATINGS: {
      REPORT: '/insights/ratings_report.csv',
      COUNTS: '/insights/ratings_counts',
      HIGHEST_RATED_LESSONS: '/insights/rated_lessons/highest',
      LOWEST_RATED_LESSONS: '/insights/rated_lessons/lowest',
      SATISFACTION_OVER_TIME: '/insights/satisfaction_over_time',
    },
    OVERDUE: {
      REPORT: '/insights/assignments_report/incomplete.csv',
      ASSIGNMENTS_COUNT: '/insights/overdue_assignments_count',
      CONTENTS: '/insights/overdue_assignments/contents',
      USERS: '/insights/overdue_assignments/users',
      GROUPS: '/insights/overdue_assignments/groups',
    },
    LESSON_VIEWS: {
      COUNT: '/insights/lesson_views_count',
      WEEKLY_LESSON_VIEWS: '/insights/weekly_lesson_views',
      MOST_ACTIVE_LESSONS: '/insights/most_active_lessons',
    },
    COMPLETED_ASSIGNMENTS: {
      REPORT: '/insights/assignments_report/completed.csv',
      COUNT: '/insights/completed_assignments_count',
      WEEKLY_ASSIGNMENTS: '/insights/weekly_assignments',
    },
    SEARCH: {
      REPORT: '/insights/search_summary_report.csv',
      COUNT: '/insights/search_count',
      TOP_SEARCHES: '/insights/top_searches',
      TOP_NULL_SEARCHES: '/insights/top_searches_with_no_results',
      SEARCHES_OVER_TIME: '/insights/searches_over_time',
    },
    ACTIVE_USERS: {
      REPORT: '/insights/active_users_report.csv',
      COUNT: '/insights/active_users_count',
      ACTIVE_USERS_OVER_TIME: '/insights/active_users_over_time',
      MOST_ACTIVE_USERS: '/insights/most_active_users',
      MOST_ACTIVE_GROUPS: '/insights/most_active_groups',
      MOST_ACTIVE_NON_ADMIN: '/insights/most_active_users?exclude_roles=admin',
    },
    CERTIFICATION_REPORT: '/insights/certifications_report.csv',
    ALL_LESSONS_REPORT: '/insights/all_lessons_report.csv',
    LATEST_ACTIVITIES: '/insights/latest_activities',
    TAGS: {
      LESSON_ACTIVITY: '/insights/lesson_summary_report.csv',
      TAG_ACTIVITY: '/insights/tag_summary_report.csv',
    },
    KNOWLEDGE_CHECKS: {
      ATTEMPTS_REPORT: '/insights/knowledge_checks_attempts_report.csv',
      CORRECTNESS_REPORT: '/insights/knowledge_checks_correctness_report.csv',
    },
    PRACTICE: {
      HIGHEST_RATED_FEEDBACK_CRITERIA:
        '/insights/practice/highest_rated_feedback_criteria',
      LOWEST_RATED_FEEDBACK_CRITERIA:
        '/insights/practice/lowest_rated_feedback_criteria',
      FEEDBACK_CRITERIA_RATINGS_COUNT:
        '/insights/practice/total_rated_feedback_criteria',
      REPORT: '/insights/practice/all_feedback_criteria_ratings_report.csv',
    },
    ON_DEMAND_PRACTICE: {
      SCENARIOS_REPORT: '/insights/on_demand_practice_scenarios_report.csv',
      USERS_REPORT: '/insights/on_demand_practice_users_report.csv',
    },
    SMS: {
      OUTBOUND_REPORT: '/insights/outbound_sms_report.csv',
      INBOUND_REPORT: '/insights/inbound_sms_report.csv',
    },
    PRACTICE_FEEDBACK: {
      LEARNERS_REQUESTING_FEEDBACK_REPORT:
        '/insights/learners_requesting_feedback_report.csv',
      LEARNERS_RECEIVING_FEEDBACK_REPORT:
        '/insights/learners_receiving_feedback_report.csv',
    },
    V2: '/v2/insights',
  },
  TRAINING: {
    EVENTS: '/training/events',
    EVENT_SESSIONS: eventId => `/training/events/${eventId}/sessions`,
    EVENT_SESSION: (eventId, sessionId) =>
      `/training/events/${eventId}/sessions/${sessionId}`,
    EVENT: id => `/training/events/${id}`,
    EVENT_SET_RSVP: eventId =>
      `/training/events/${eventId}/attendances/register`,
    ICS_DOWNLOAD: eventId => `/training/events/${eventId}/calendar.ics`,
    SESSION_SET_ATTENDANCE: (eventId, sessionId) =>
      `/training/events/${eventId}/sessions/${sessionId}/attendances/register`,
  },
  IN_PERSON_TRAINING: {
    EVENTS_INDEX: '/in_person_training/events#index',
    TRAINERS: '/in_person_training/training_event/trainers',
    CREATORS: '/in_person_training/training_event/creators',
    EVENTS: '/in_person_training/events',
    EVENT: id => `/in_person_training/events/${id}`,
    ARCHIVE_EVENT: id => `/in_person_training/events/${id}/archive`,
    RESTORE_EVENT: id => `/in_person_training/events/${id}/restore`,
    EVENT_GUESTS: eventId => `/training/events/${eventId}/guests`,
    SESSION_GUESTS: (eventId, sessionId) =>
      `/training/events/${eventId}/sessions/${sessionId}/guests`,
    EVENT_UNREGISTERED: id => `/in_person_training/events/${id}/unregistered`,
    EVENTS_DASHBOARD: '/in_person_training/events_dashboard',
    EVENTS_DASHBOARD_TABS: {
      UPCOMING: '/in_person_training/events_dashboard/upcoming',
      PAST: '/in_person_training/events_dashboard/past',
      OPEN: '/in_person_training/events_dashboard/open',
    },
    EVENT_SESSIONS: eventId => `/in_person_training/events/${eventId}/sessions`,
    EVENT_SESSION: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}`,
    EVENT_INVITATIONS: eventId =>
      `/in_person_training/events/${eventId}/invitations`,
    RSVP_ALL_GUESTS: eventId => `/training/events/${eventId}/attendances`,
    RSVP_SINGLE_GUESTS: (eventId, sessionId) =>
      `/training/events/${eventId}/sessions/${sessionId}/attendances`,
    SESSION_INVITATIONS: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/invitations`,
    SESSION_ATTENDANCES: sessionId =>
      `/in_person_training/sessions/${sessionId}/attendances/`,
    SESSION_ATTENDANCE: (sessionId, attendanceId) =>
      `/in_person_training/sessions/${sessionId}/attendances/${attendanceId}`,
    SESSION_TAKE_ATTENDANCE: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/take_attendance`,
    NEW_EVENT: '/in_person_training/events/new',
    EVENT_SET_ATTENDANCE: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/register`,
    INVITATION_PREVIEW: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/invitation_preview`,
    ICS_LINK: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/calendar.ics`,
    JOIN_MEETING: (eventId, sessionId) =>
      `/in_person_training/attendee/events/${eventId}/sessions/${sessionId}/join_meeting`,
    UPDATE_EMAIL: '/in_person_training/event_change_management/update_email',
    PEOPLE: '/in_person_training/people',
    EVENT_VISIBILITY_GROUPS: eventId =>
      `/in_person_training/events/${eventId}/event_visibility_groups`,
    EVENT_VISIBILITY_GROUP: (eventId, visibilityGroupId) =>
      `/in_person_training/events/${eventId}/event_visibility_groups/${visibilityGroupId}`,
    ASSOCIATED_CONTENTS: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/associated_contents.json`,
    ADD_ASSOCIATED_CONTENT: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/associated_contents`,
    REMOVE_ASSOCIATED_CONTENT_ITEM: (eventId, sessionId, contentId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/associated_contents/${contentId}.json`,
    ASSIGN_CONTENT: (eventId, sessionId) =>
      `/in_person_training/events/${eventId}/sessions/${sessionId}/assign_associated_contents`,
    ATTENDEE: {
      EVENT_REGISTRATION: eventId =>
        `/in_person_training/attendee/events/${eventId}/register`,
    },
  },
  ZENDESK_TICKETS: '/practice/zendesk/tickets',
  PRACTICE: {
    PEER_FEEDBACK: '/peer_feedback',
    PEER_FEEDBACKS: '/practice/peer_feedbacks',
    TECHNICAL_ISSUES_MAILER: {
      CREATE: questionId =>
        `/practice/questions/${questionId}/technical_issues`,
    },
    ROLE_PLAY: {
      PARTNER_FEEDBACK: token => `/practice/role_play_guides/${token}/feedback`,
      PARTNER_GUIDE: token =>
        `/practice/role_play_guides/${token}/partner_guide`,
      PARTNER_GUIDE_PREVIEW: rolePlayId =>
        `/practice/preview/role_play_guides/${rolePlayId}/partner_guide`,
    },
  },
  LEARN_SHARED_LIBRARY_GROUPS_FOR_CONTENTS:
    'learn/shared_library_groups_for_contents',
  LEARN_UPDATE_LIBRARY_CONTENTS: 'learn/update_library_contents',
  LEARN_SHARED_OWNERS_FOR_CONTENTS: 'learn/shared_owners_for_contents',
  LEARN_UPDATE_OWNERS: 'learn/update_owners',
  LEARN_SHARED_TAGS_FOR_CONTENTS: 'learn/shared_tags_for_contents',
  LEARN_UPDATE_TAGS: 'learn/update_tags',
  WORKSPACES: {
    INDEX: '/workspaces',
    WORKSPACE: id => `/workspaces/${id}`,
  },
  ZIGGEO_TOKEN: {
    SHOW: '/ziggeo/token',
  },
  ON_DEMAND_PRACTICE: {
    CREATOR: {
      INDEX: '/practice/scenarios',
      SHOW: (id, groupId) =>
        `/practice/scenarios/${id}${groupId ? '?group_id=' + groupId : ''}`,
      DELETE: id => `/practice/scenarios/${id}`,
      CONTAINER: {
        ARCHIVE: id => `/practice/containers/${id}/archive`,
        RESTORE: id => `/practice/containers/${id}/restore`,
      },
      UPDATE: id => `/practice/scenarios/${id}`,
      EDIT: id => `/practice/scenarios/${id}/edit`,
      CONVERSATIONS: {
        QUESTIONS: {
          INTERACTIONS: {
            UPDATE: (questionId, id) =>
              `/questions/${questionId}/interactions/${id}`,
            CREATE: questionId => `/questions/${questionId}/interactions`,
            DELETE: (questionId, id) =>
              `/questions/${questionId}/interactions/${id}`,
            METRICS: (questionId, groupId) =>
              `/questions/${questionId}/interaction_metrics${createQuery({
                group_id: groupId,
              })}`,
            METRICS_DETAILS: (questionId, interactionId, groupId) =>
              `/questions/${questionId}/interaction_metrics/${interactionId}${createQuery(
                { group_id: groupId },
              )}`,
            RESPONSES: {
              CREATE: (questionId, interactionId) =>
                `/questions/${questionId}/interactions/${interactionId}/responses`,
              UPDATE: (questionId, interactionId, id) =>
                `/questions/${questionId}/interactions/${interactionId}/responses/${id}`,
              DELETE: (questionId, interactionId, id) =>
                `/questions/${questionId}/interactions/${interactionId}/responses/${id}`,
            },
          },
        },
        INTERACTIONS: {
          CREATE: scenarioId =>
            `/practice/scenarios/${scenarioId}/interactions`,
          UPDATE: (scenarioId, id) =>
            `/practice/scenarios/${scenarioId}/interactions/${id}`,
          DELETE: (scenarioId, id) =>
            `/practice/scenarios/${scenarioId}/interactions/${id}`,
          METRICS: (scenarioId, groupId) =>
            `/practice/scenarios/${scenarioId}/interaction_metrics${createQuery(
              { group_id: groupId },
            )}`,
          METRICS_DETAILS: (scenarioId, interactionId, groupId) =>
            `/practice/scenarios/${scenarioId}/interaction_metrics/${interactionId}${createQuery(
              { group_id: groupId },
            )}`,
          RESPONSES: {
            CREATE: (scenarioId, id) =>
              `/practice/scenarios/${scenarioId}/interactions/${id}/responses`,
            UPDATE: (scenarioId, interactionId, id) =>
              `/practice/scenarios/${scenarioId}/interactions/${interactionId}/responses/${id}`,
            DELETE: (scenarioId, interactionId, id) =>
              `/practice/scenarios/${scenarioId}/interactions/${interactionId}/responses/${id}`,
          },
        },
        METADATA: (scenarioId, id) =>
          `/practice/scenarios/${scenarioId}/metadata/${id}`,
      },
      ENGAGEMENTS: {
        COMPLETIONS: (id, groupId) =>
          `/practice/scenarios/${id}/completions${
            groupId ? '?group_id=' + groupId : ''
          }`,
        VIEWS: (id, groupId) =>
          `/practice/scenarios/${id}/views?group_id=${groupId}`,
        UNIQUE_VIEWS: (id, groupId) =>
          `/practice/scenarios/${id}/unique_views?group_id=${groupId}`,
      },
      FEEDBACK_CONTRIBUTOR_OPTIONS: {
        INDEX: scenarioId =>
          `/practice/scenarios/${scenarioId}/feedback_contributor_options`,
        AUTOCOMPLETE_FEEDBACK_CONTRIBUTOR: scenarioId =>
          `/practice/scenarios/${scenarioId}/feedback_contributor_options/autocomplete_feedback_contributor`,
      },
      FEEDBACK_CRITERIA: {
        INDEX: scenarioId =>
          `/practice/scenarios/${scenarioId}/feedback_criteria`,
      },
      ATTEMPTS: {
        INDEX: (scenarioId, groupId) =>
          `/practice/scenarios/${scenarioId}/attempts${
            groupId ? '?group_id=' + groupId : ''
          }`,
        USER: (scenarioId, userId) =>
          `/practice/scenarios/${scenarioId}/attempts/users/${userId}`,
      },
    },
    LEARNING: {
      ATTEMPTS: {
        INDEX: scenarioId =>
          `/learning/practice/scenarios/${scenarioId}/attempts`,
        SHOW: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}`,
        FEEDBACK: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/feedback`,
        REQUEST_FEEDBACK: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/request_feedback`,
        ATTEMPT_FEEDBACKS: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/attempt_feedbacks`,
        SUBMIT_FEEDBACK_REQUEST: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/feedback_requests`,
        ALL: '/learning/practice/attempts',
        GIVE_FEEDBACK: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/give_feedback`,
        FEEDBACK_CONTRIBUTOR_OPTIONS: (scenarioId, attemptId) =>
          `/learning/practice/scenarios/${scenarioId}/attempts/${attemptId}/feedback_contributor_options`,
      },
      ALL_FEEDBACK_REQUESTS: '/learning/practice/feedback_requests',
      INDEX: pageNumber => `/learning/practice/scenarios?page=${pageNumber}`,
      SHOW: (id, tab) =>
        `/learning/practice/scenarios/${id}${tab ? `?tab=${tab}` : ''}`,
    },
    PREVIEW: {
      SHOW: scenarioId => `/preview/practice/scenarios/${scenarioId}`,
    },
    CONTAINER_VISIBILITY_GROUPS: '/practice/container_visibility_groups',
    CONTAINER_VISIBILITY_GROUP: id =>
      `/practice/container_visibility_groups/${id}`,
  },
  ANALYZER_RIGHTS: {
    LESSONS: '/analyzer_rights_lessons',
    PATHS: '/analyzer_rights_paths',
  },
  TRIGGER: id => `/triggers/${id}`,
  TRIGGERS: {
    INDEX: '/triggers',
    EDIT: id => `/triggers/${id}/edit`,
  },
  TUTORIAL: {
    CHECK: '/tutorial/check',
    DISMISS: '/tutorial/dismiss',
  },
  NOTIFICATION_SUBSCRIPTIONS: 'notification_subscriptions',
  NOTIFICATION_SUBSCRIPTION: id => `/notification_subscriptions/${id}`,
  DEMONSTRATION_INDEX: '/demonstrations',
  DEMONSTRATION: (pathId, id) => `/path/${pathId}/demonstration/${id}`,
  DEMONSTRATION_GRADES: query => `/demonstration_grades${createQuery(query)}`,
  DEMONSTRATION_LINK_SUBMISSIONS: (pathId, demonstrationId) =>
    `/path/${pathId}/demonstration/${demonstrationId}/submissions`,
  UPDATE_DEMONSTRATION: id => `/demonstrations/${id}`,
  DEMONSTRATION_PROGRESSES_GRADE: progressId =>
    `/demonstration_progresses/${progressId}/grade`,
  CONVERT_PATH_TO_CERTIFICATION: pathId => `/paths/${pathId}`,
  CREATE_CERTIFICATION: 'certification',
  COMPLETED_CERTIFICATIONS: userId =>
    `/users/${userId}/completed_certifications`,
  CERTIFICATE: pathId => `/certificate/${pathId}`,
  OTHER_CERTIFICATION_LEARNERS: certificationId =>
    `${certificationId}/other_learners`,
  ORGANIZATIONS: {
    LINKED_USERS: '/linked_users',
    COMPANIES: '/organizations/companies',
  },
  EDITOR: {
    ASSOCIATED_COMMENTS: elementId =>
      `/questions/${elementId}/associated_comments`,
  },
  ENABLED_INTEGRATIONS: {
    CHECK: '/enabled_integrations/check',
  },
  ASSESSMENTS: {
    ANSWER: (answerId, assignmentType, lessonId, pathId) => {
      const queryArgs = {};
      if (assignmentType) queryArgs.assignment_type = assignmentType;
      if (lessonId) queryArgs.lesson_id = lessonId;
      if (pathId) queryArgs.path_id = pathId;
      return `/assessments/answer/${answerId}${paramsToQueryString(queryArgs)}`;
    },
    FEEDBACK: (answerId, assignmentType, lessonId, pathId) => {
      const queryArgs = {};
      if (assignmentType) queryArgs.assignment_type = assignmentType;
      if (lessonId) queryArgs.lesson_id = lessonId;
      if (pathId) queryArgs.path_id = pathId;
      return `/assessments/answer/${answerId}/confidenceFeedback/${paramsToQueryString(
        queryArgs,
      )}`;
    },
  },
  DEMUX_APPS: {
    CONNECTIONS: id => `/external_apps/connections/${id}`,
    CONNECTIONS_NEW: id => `/external_apps/apps/${id}/connections/new`,
  },
  ASSIGNMENTS: {
    SET_DUE_DATE: id => `/assignments/${id}`,
    BULK_UPDATE: '/assignments/bulk_update',
    BULK_REMIND: '/assignments/bulk_remind',
    BULK_REMINDER: '/assignments/send_bulk_reminder',
    BULK_DESTROY: '/assignments/bulk_destroy',
    BULK_MARK_COMPLETE: '/assignments/bulk_mark_complete',
  },
  JOURNEY: {
    RESOURCE: id => `/journey/${id}`,
    CREATE: '/journey/create',
    EDIT: id => `/journey/${id}/edit/`,
    START: id => `/journey/${id}/start_journey`,
    GET: '/journeys',
    COMPLETE_STEP: id => `/journey/${id}/complete_step`,
    PUBLISH_JOURNEY: id => `/journey/${id}/publish_journey`,

    STEPS: {
      CREATE: journeyId => `/journey/${journeyId}/steps`,
      RESOURCE: (journeyId, stepId) => `/journey/${journeyId}/steps/${stepId}`,
    },

    TASK: {
      CREATE: journeyId => `/journey/${journeyId}/tasks`,
      RESOURCE: (journeyId, taskId) => `/journey/${journeyId}/tasks/${taskId}`,
    },
  },
  SEISMIC: {
    PLAYER_CONFIGURATION: id => `/seismic/contents/${id}/player_configuration`,
    REFRESH_TOKEN: '/seismic/refresh_token',
  },
};

export const ui = {
  NOTIFICATION_SUCCESS_DELAY: 1500,
  INDEX_ROWS_PER_PAGE: [10, 25, 50, 100],
};

export const ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
};

export const COLUMNS = {
  NAME: 'name',
  TITLE: 'title',
  LAST_MODIFIED: 'last_modified_at',
};

export const TYPES = {
  LESSON: 'Lesson',
  USER: 'User',
  GROUP: 'Group',
  RESOURCE: 'Resource',
  PATH: 'LearningPaths::Path',
  WAITSTEP: 'LearningPaths::WaitStep',
  PLACEHOLDER: 'LearningPaths::Placeholder',
  DEMONSTRATION: 'Demonstration',
  EXTERNAL_LINK: 'ExternalLink',
  COLLAPSED_CONTENT: 'LearningPaths::CollapsedContent',
  CERTIFICATION: 'Certification',
  TASK: 'Task',
};

export const TYPE_ICONS = {
  Group: 'group',
  Path: 'path',
  'LearningPaths::Path': 'path',
  Demonstration: 'demonstration',
  Lesson: 'lesson',
  Certification: 'new-certification-badge',
  Scorm: 'scorm',
};

export const LEARN_TYPES = Object.freeze({
  LESSON: 'lesson',
  PATH: 'path',
  TAG: 'tag',
  TOPIC: 'topic',
  ELEMENT: 'element',
  SECTION: 'section',
});

export const ELEMENT_TYPES = {
  REVEAL: 'Reveal',
  FLIPCARD: 'Flipcard',
};

export const EVENTS = {
  SET_STEP_CONNECTOR_POSITION: 'path:setStepsConnectorPosition',
  ASSIGN_MODAL_TOGGLE: 'assignModal:toggle',
  ASSIGNMENT_CREATED: 'assignModal:assignmentCreated',
};

export const PATH_MODES = {
  EDIT: 'edit',
  VIEW: 'view',
  REPORTING: 'reporting',
};

export const ASYNC_REPORTS_TYPE = {
  ALL_ACTIVITIES_REPORT: 'all_activities_report',
  EXPORT_ALL_PEOPLE: 'export_all_people',
  EXPORT_KNOWLEDGE_CHECK_ATTEMPTS: 'export_knowledge_check_attempts',
  EXPORT_LEARNERS_FOR_GROUP_TAG_REPORT: 'export_learners_for_group_tag_report',
};

export const MASS_ACTION = {
  ARCHIVE: 'archive',
  RESTORE: 'restore',
  MASS_LEARN: 'mass_learn',
  MASS_OWNERSHIP: 'mass_ownership',
  MASS_TAGGING: 'mass_tagging',
};

// TODO: As we implement more video conferencing integrations (Webex, Google, etc..) we should consider refactoring
//     oauth initiation/configuration into it's own component so it can be implemented elsewhere within the app.
export const VIDEO_CONFERENCE_TYPES = {
  NONE: 'none',
  OTHER: 'other',
  ZOOM: 'zoom',
};

export const INTEGRATION_OAUTH = {
  REDIRECT_URI: (host, integrationName) =>
    `https://${host}/integrations/oauth/${integrationName}/callback`,
  ZOOM: {
    AUTHORIZE: oauthParms => `https://zoom.us/oauth/authorize?${oauthParms}`,
  },
};

export const EMBED_ROUTES = {
  WISTIA_IFRAME: (wistiaId, brandingColor) =>
    `https://fast.wistia.net/embed/iframe/${wistiaId}?playerColor=${brandingColor}`,
};

export const DIRECTION = {
  UP: 1,
  DOWN: 2,
};

export const SCREEN_SIZES = {
  SM: 768,
  MD: 1060,
  LG: 1200,
};

export const SORTING = {
  MAX_CHARACTERS: 65,
};

export const MATCHING = {
  MAX_CHARACTERS: 90,
};

export const REACT_QUERY_STATUS = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success',
};

export const LESSON_EVENT = {
  RSVP_STATUS: 'will_attend',
};
